import { useState } from "react";
import { Link, useParams } from "react-router";
import {
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useTranslate,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { PageFrame } from "@/tenax/procedures/ProcedurePages";
import { fullName, useTeam } from "@/tenax/tasks/taskData";
import { RacketActionDialog, type RacketAction } from "./RacketActionDialog";
import { PrintLabelButton, RacketBarcode } from "./RacketLabel";
import { StatusBadge } from "./RacketPages";
import {
  RACKET_RESOURCES,
  modelOf,
  uploadRacketPhoto,
  useRacketCommand,
  useRacketOverview,
  useVariants,
  type Loan,
  type Movement,
  type TestRacket,
} from "./racketData";
import { HolderName, LoanRow } from "./RelatedLoans";
import { useDay } from "./racketHooks";

// Card of a test racket: status, holder and days, label, photo, the
// movements (return, transfer, status, correction) and the history
export const TestRacketShow = () => {
  const translate = useTranslate();
  const { id } = useParams();
  const { data: racket } = useGetOne<TestRacket>("test_rackets", {
    id: Number(id),
  });
  const { data: overview = [] } = useRacketOverview({ id });
  const { data: variants = [] } = useVariants();
  const { data: loan } = useGetOne<Loan>(
    "test_racket_loans",
    { id: racket?.current_loan_id ?? 0 },
    { enabled: racket?.current_loan_id != null },
  );
  const run = useRacketCommand();
  const [action, setAction] = useState<RacketAction | null>(null);
  const back = (
    <Button variant="ghost" size="icon" asChild>
      <Link to="/test_rackets" aria-label={translate("ra.action.back")}>
        <ArrowLeft className="size-5" />
      </Link>
    </Button>
  );
  if (!racket)
    return (
      <PageFrame title="…" back={back}>
        {null}
      </PageFrame>
    );
  const row = overview[0];
  const model = modelOf(racket, variants);

  return (
    <PageFrame title={racket.code} back={back}>
      <Card>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-lg font-medium">{model}</span>
              <StatusBadge status={racket.status} isOverdue={row?.is_overdue} />
              {racket.status === "on_loan" ? (
                <span className="text-sm">
                  {translate("tenax.rackets.held_by")}{" "}
                  <HolderName
                    contactId={racket.holder_contact_id}
                    companyId={racket.holder_company_id}
                  />
                </span>
              ) : null}
              {row ? (
                <span className="text-sm text-muted-foreground">
                  {translate("tenax.rackets.days", {
                    smart_count: row.days_since ?? 0,
                  })}
                </span>
              ) : null}
              {racket.condition ? (
                <span className="text-sm">
                  {translate("tenax.rackets.fields.condition")}:{" "}
                  {racket.condition}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col items-center gap-2">
              <RacketBarcode code={racket.code} />
              <PrintLabelButton code={racket.code} model={model} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {racket.status === "on_loan" ? (
              <>
                <Button
                  type="button"
                  onClick={() =>
                    run(
                      "tenax_return_test_racket",
                      { p_racket_id: racket.id },
                      "tenax.rackets.returned",
                    )
                  }
                >
                  {translate("tenax.rackets.actions.return")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAction("transfer")}
                >
                  {translate("tenax.rackets.actions.transfer")}
                </Button>
              </>
            ) : null}
            <Button
              type="button"
              variant="outline"
              onClick={() => setAction("status")}
            >
              {translate("tenax.rackets.actions.status")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAction("correction")}
            >
              {translate("tenax.rackets.actions.correction")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loan ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold">
            {translate("tenax.rackets.current_loan")}
          </h2>
          <ul>
            <LoanRow loan={loan} />
          </ul>
        </section>
      ) : null}

      <RacketPhotoField racket={racket} />
      <Separator />
      <RacketHistory racketId={racket.id} />

      {action ? (
        <RacketActionDialog
          racket={racket}
          action={action}
          onClose={() => setAction(null)}
        />
      ) : null}
    </PageFrame>
  );
};

const RacketPhotoField = ({ racket }: { racket: TestRacket }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const onFile = async (file?: File) => {
    if (!file) return;
    setSaving(true);
    try {
      const photo = await uploadRacketPhoto(file);
      await dataProvider.update("test_rackets", {
        id: racket.id,
        data: { photo },
        previousData: racket,
      });
      await Promise.all(
        RACKET_RESOURCES.map((resource) =>
          queryClient.invalidateQueries({ queryKey: [resource] }),
        ),
      );
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
    setSaving(false);
  };

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">
        {translate("tenax.rackets.fields.photo")}
      </h2>
      {racket.photo ? (
        <img
          src={racket.photo.src}
          alt={racket.code}
          className="max-h-64 w-fit rounded-md border object-contain"
        />
      ) : null}
      <Label htmlFor="racket-photo" className="sr-only">
        {translate("tenax.rackets.fields.photo")}
      </Label>
      <Input
        id="racket-photo"
        type="file"
        accept="image/*"
        disabled={saving}
        onChange={(event) => onFile(event.target.files?.[0])}
      />
    </section>
  );
};

const RacketHistory = ({ racketId }: { racketId: number }) => {
  const translate = useTranslate();
  const day = useDay();
  const { data: team = [] } = useTeam();
  const { data: movements = [] } = useGetList<Movement>(
    "test_racket_movements",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "moved_at", order: "DESC" },
      filter: { racket_id: racketId },
    },
  );
  return (
    <section
      aria-label={translate("tenax.rackets.history")}
      className="flex flex-col gap-2"
    >
      <h2 className="text-sm font-semibold">
        {translate("tenax.rackets.history")}
      </h2>
      {movements.length ? (
        <ol className="flex flex-col gap-1">
          {movements.map((movement) => (
            <li
              key={movement.id}
              className="flex flex-col rounded-md border px-3 py-1.5 text-sm"
            >
              <span className="flex flex-wrap gap-x-2">
                <span className="text-muted-foreground">
                  {day(movement.moved_at)}
                </span>
                <span className="font-medium">
                  {translate(`tenax.rackets.kind.${movement.kind}`)}
                </span>
                <span>
                  → {translate(`tenax.rackets.status.${movement.status_to}`)}
                </span>
                {movement.to_contact_id != null ||
                movement.to_company_id != null ? (
                  <HolderName
                    contactId={movement.to_contact_id}
                    companyId={movement.to_company_id}
                  />
                ) : null}
                <span className="text-muted-foreground">
                  {fullName(
                    team.find((member) => member.id === movement.moved_by),
                  )}
                </span>
              </span>
              {movement.note ? <span>{movement.note}</span> : null}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("tenax.rackets.no_history")}
        </p>
      )}
    </section>
  );
};
